import { useState } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import backend from "../services/backend";
import Tag from "./Tag";
import { Button } from "../catalyst/button";
import { Input } from "../catalyst/input";
import { usePlatformData } from "../hooks/usePlatformData";

interface DocumentTagsCardProps {
  selectedIds: Set<string>;
  selectedDocumentsTags: string[];
  onTagsUpdated: () => void | Promise<void>;
}

export default function DocumentTagsCard({
  selectedIds,
  selectedDocumentsTags,
  onTagsUpdated,
}: DocumentTagsCardProps) {
  const { tags } = usePlatformData();
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [isUpdatingTags, setIsUpdatingTags] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);

  const handleAddTags = async () => {
    if (!newTag.trim() || selectedIds.size === 0) return;

    const tagsToAdd = newTag
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    if (tagsToAdd.length === 0) return;

    setIsUpdatingTags(true);
    setTagError(null);

    try {
      await backend.updateBulkTags(Array.from(selectedIds), tagsToAdd, "add");
      await onTagsUpdated();
      setNewTag("");
      setShowTagInput(false);
    } catch (err) {
      console.error("Failed to add tags:", err);
      setTagError("Failed to add tags");
    } finally {
      setIsUpdatingTags(false);
    }
  };

  const handleRemoveTags = async (tagsToRemove: string[]) => {
    if (selectedIds.size === 0 || tagsToRemove.length === 0) return;

    setIsUpdatingTags(true);
    setTagError(null);

    try {
      await backend.updateBulkTags(
        Array.from(selectedIds),
        tagsToRemove,
        "remove"
      );
      await onTagsUpdated();
    } catch (err) {
      console.error("Failed to remove tags:", err);
      setTagError("Failed to remove tags");
    } finally {
      setIsUpdatingTags(false);
    }
  };

  return (
    <div className="bg-zinc-100 sm:rounded-lg overflow-hidden h-fit dark:bg-zinc-800">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-zinc-100">
            Manage Tags
          </h3>
        </div>

        <div className="mt-2">
          <div className="flex flex-wrap gap-2">
            {selectedDocumentsTags.map((tag) => (
              <Tag
                key={tag}
                tag={tag}
                onRemove={() => handleRemoveTags([tag])}
                disabled={isUpdatingTags}
              />
            ))}
            {!showTagInput ? (
              <Button
                plain
                onClick={() => setShowTagInput(true)}
                disabled={isUpdatingTags}
                buttonClassName="rounded-full px-2 py-0.5 sm:px-2 sm:py-0.5 gap-x-1 text-xs sm:text-xs font-medium whitespace-nowrap text-blue-600 dark:text-blue-400 *:data-[slot=icon]:size-3 sm:*:data-[slot=icon]:size-3"
              >
                <PlusIcon />
                Add Tag
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTags();
                    } else if (e.key === "Escape") {
                      setShowTagInput(false);
                      setNewTag("");
                    }
                  }}
                  list="available-tags"
                  placeholder="New tag..."
                  autoFocus
                />
                <datalist id="available-tags">
                  {(tags.data?.tags ?? [])
                    .filter((tag) => !selectedDocumentsTags.includes(tag))
                    .map((tag) => (
                      <option key={tag} value={tag} />
                    ))}
                </datalist>
                <Button
                  plain
                  onClick={handleAddTags}
                  disabled={!newTag.trim() || isUpdatingTags}
                  buttonClassName="px-2 py-1 sm:px-2 sm:py-1 text-xs sm:text-xs font-medium text-blue-600 dark:text-blue-400"
                >
                  Add
                </Button>
                <Button
                  plain
                  onClick={() => {
                    setShowTagInput(false);
                    setNewTag("");
                  }}
                  buttonClassName="px-2 py-1 sm:px-2 sm:py-1 text-xs sm:text-xs font-medium text-gray-600 dark:text-zinc-400"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
          {tagError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {tagError}
            </p>
          )}
          {isUpdatingTags && (
            <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
              Updating tags...
            </p>
          )}
          {selectedDocumentsTags.length === 0 && !showTagInput && (
            <p className="mt-2 text-sm text-gray-500 dark:text-zinc-400">
              No tags found in selected documents. Add some tags to get started.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
